/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarClock } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useReviewsDue } from '../../lib/queries/reviews.js';

/** Amber "reviews due" reminder over the therapist plans list (BRD M01). */
export function ReviewsDueBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const due = useReviewsDue();
  const list = due.data ?? [];
  if (list.length === 0) {
    return null;
  }
  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
        <CalendarClock className="h-4 w-4" aria-hidden="true" />
        {t('therapist.review.dueBanner', { count: list.length })}
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {list.map((plan) => (
          <li key={plan.id}>
            <Link
              to={`/therapist/plans/${plan.id}`}
              className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
            >
              {plan.clientName}
              {plan.reviewDate !== null && (
                <span className="text-amber-500">· {plan.reviewDate}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
